import React, { useState } from 'react';
import { Modal, Button } from '../ui';
import { X, Split } from 'lucide-react';
import { safeArray } from '../../utils/safe';
import type { Order } from '../../types/@types/order';
import { formatCurrency } from '../../utils/format-currency';

interface SplitOrderModalProps {
  open: boolean;
  order: Order | null;
  onClose: () => void;
  onConfirm: (itemIds: (string | number)[]) => void;
  loading?: boolean;
}

export const SplitOrderModal: React.FC<SplitOrderModalProps> = ({
  open,
  order,
  onClose,
  onConfirm,
  loading,
}) => {
  const [selectedIds, setSelectedIds] = useState<(string | number)[]>([]);

  const toggleItem = (id: string | number) => {
    setSelectedIds((prev) => {
      const safePrev = safeArray(prev);
      return safePrev.includes(id) ? safePrev.filter((i) => i !== id) : [...safePrev, id];
    });
  };

  const handleConfirm = () => {
    if (selectedIds.length === 0) return;
    onConfirm(selectedIds);
  };

  if (!order) return null;

  // Only show items that are not cancelled
  const items = safeArray(order.items).filter(i => i.status !== 'CANCELLED');

  return (
    <Modal open={open} onClose={onClose} titleId="split-modal-title" className="max-w-2xl">
      <div className="flex items-start justify-between border-b border-border px-6 py-4">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-50 dark:bg-emerald-950/30 text-emerald-600 dark:text-emerald-450 border border-emerald-100 dark:border-emerald-900/30 shadow-sm">
            <Split className="h-5 w-5" />
          </div>
          <div>
            <h2 id="split-modal-title" className="text-lg font-bold text-text tracking-tight">
              Dividir Cuenta
            </h2>
            <p className="text-sm text-text-muted">Selecciona los productos para mover a una nueva cuenta</p>
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={onClose} className="h-9 w-9 rounded-full">
          <X className="h-4 w-4" />
        </Button>
      </div>

      <div className="p-6 space-y-4 max-h-[60vh] overflow-y-auto">
        <div className="space-y-2">
          {items.map((item) => (
            <div
              key={item.id}
              className={`flex items-center justify-between p-3.5 rounded-xl border transition-all duration-200 cursor-pointer hover:shadow-sm ${
                selectedIds.includes(item.id)
                  ? 'bg-emerald-50/35 border-emerald-500 shadow-sm'
                  : 'bg-white border-border hover:bg-gray-50 hover:border-slate-300'
              }`}
              onClick={() => toggleItem(item.id)}
            >
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={selectedIds.includes(item.id)}
                  onChange={() => toggleItem(item.id)}
                  className="h-4.5 w-4.5 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500 cursor-pointer focus:ring-offset-0"
                />
                <div>
                  <p className="font-semibold text-text">{item.product?.name || 'Producto'}</p>
                  <p className="text-xs text-text-muted mt-0.5">Cantidad: {item.quantity}</p>
                </div>
              </div>
              <span className="font-bold text-text">
                {formatCurrency(item.line_subtotal || (item.product?.price ? item.product.price * item.quantity : 0) || ((item as any).unitPrice ? (item as any).unitPrice * item.quantity : 0) || (item.base_price ? item.base_price * item.quantity : 0))}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className="border-t border-border px-6 py-4 flex items-center justify-between gap-4">
        <div className="text-sm font-semibold text-text-muted">
          Seleccionados: <span className="text-text font-bold">{selectedIds.length}</span> ítems
        </div>
        <div className="flex gap-3">
          <Button variant="outline" onClick={onClose} className="rounded-full px-5">
            Cancelar
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={selectedIds.length === 0 || selectedIds.length === items.length || loading}
            isLoading={loading}
            className={`rounded-full px-6 bg-gradient-to-r from-emerald-600 to-teal-650 text-white shadow-md shadow-emerald-500/10 hover:from-emerald-500 hover:to-teal-550 hover:shadow-lg transition-all duration-300 active:scale-[0.98] ${
              selectedIds.length === 0 || selectedIds.length === items.length || loading
                ? 'from-slate-200 to-slate-200 text-slate-400 shadow-none dark:from-neutral-800 dark:to-neutral-800 dark:text-neutral-500'
                : ''
            }`}
          >
            <Split className="mr-2 h-4 w-4" />
            Crear Nueva Cuenta
          </Button>
        </div>
      </div>
    </Modal>
  );
};
